import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../../hooks/useIsMobile";
import axios from "axios";
import { Trash2, SquarePen, ShieldCheck, KeyRound } from "lucide-react";
import AgGridActionMenu from "../../../components/AgGridAction/AgGridAction";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import TitleCard from "../../../components/TitleCard/TitleCard";
import Loader from "../../../components/Loader/Loader";
import ReusableModal from "../../../components/Modal/ReusableModal";
import UserForm from "./UserForm";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import MobileCardList from "../../../components/MobileCardList/MobileCardList";
import ActionButton from "../../../components/ActionButton/ActionButton";


const UsersList = () => {
  const navigate = useNavigate();
  const gridApiRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: "confirm",
    title: "Eliminar usuario",
    message: "¿Seguro que deseas eliminar este usuario?",
    onConfirm: null,
  });

  // MOBILE DETECTION
  const isMobile = useIsMobile();

  // COLUMN DEFINITIONS
  const components = useMemo(
    () => ({
      actionMenu: AgGridActionMenu,
    }),
    []
  );

  const [colDefs] = useState([
    { field: "_id", headerName: "ID", maxWidth: 220 },
    { field: "name", headerName: "Nombre" },
    { field: "username", headerName: "Nombre de Usuario" },
    { field: "email", headerName: "Correo electrónico" },
    { field: "age", headerName: "Edad" },
    {
      headerName: "Opciones",
      cellRenderer: "actionMenu",
      cellRendererParams: {
        onEdit: (params) => handleEditUser(params),
        onDelete: (params) => handleDeleteUser(params),
        onAssignRoles: (params) => handleAssignRoles(params),
        onAssignPermissions: (params) => handleAssignPermissions(params),
      },
      sortable: false,
      filter: false,
      width: 190,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
  ]);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      filter: false,
      floatingFilter: false,
      editable: false,
      cellStyle: { fontWeight: "normal" },
    }),
    []
  );

  // DATA SOURCE
  const createDataSource = useCallback(() => {
    return {
      getRows: async (params) => {
        setLoading(true);
        try {
          const size = params.endRow - params.startRow;
          const page = size > 0 ? params.startRow / size : 0;

          const token = localStorage.getItem("access_token");
          const url = `${process.env.REACT_APP_API_URL}/users/get-all`;

          const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            params: { page, size },
          });

          params.successCallback(response.data.data, response.data.total);
        } catch (error) {
          console.error("Error cargando usuarios:", error);
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    };
  }, []);

  // GRID READY (DESKTOP)
  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;

      if (typeof params.api.setDatasource === "function") {
        params.api.setDatasource(createDataSource());
      } else {
        params.api.setGridOption("datasource", createDataSource());
      }
    },
    [createDataSource]
  );

  const onPaginationChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const api = gridApiRef.current;
    const newPageSize = api.paginationGetPageSize();
    setPageSize((prev) => (prev !== newPageSize ? newPageSize : prev));
  }, []);

  // ACCIONES
  const handleCreateUser = () => navigate("../users-create");

  const handleEditUser = (params) => {
    const data = params?.rowData || params;
    setSelectedUser(data);
    setShowEditModal(true);
  };

  const getUserId = (params) => {
    const data = params?.data || params?.rowData || params;
    return data?._id || data?.id;
  };

  const handleAssignRoles = (params) => {
    const data = params?.data || params?.rowData || params;
    const userId = getUserId(data);
    if (userId) navigate(`../users/assign-roles/${userId}`, { state: { user: data } });
  };

  const handleAssignPermissions = (params) => {
    const data = params?.data || params?.rowData || params;
    const userId = getUserId(data);
    if (userId) navigate(`../users/assign-permissions/${userId}`, { state: { user: data } });
  };

  const handleDeleteUser = (params) => {
    // console.log("Eliminar usuario:", params);
    const data = params?.data || params;
    const id = data?._id;
    if (!id) return;

    setConfirmModal({
      isOpen: true,
      type: "confirm",
      title: "Eliminar usuario",
      message: "¿Estás Seguro que deseas eliminar este usuario?",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("access_token");
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/users/delete/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          setConfirmModal({
            isOpen: true,
            type: "success",
            title: "Usuario eliminado",
            message: "El usuario fue eliminado correctamente.",
            onConfirm: () => {
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          });

          if (gridApiRef.current?.purgeInfiniteCache) {
            setLoading(true);
            gridApiRef.current.purgeInfiniteCache();
          }
          if (isMobile) {
            setMobilePage(0); // opcional: volver a la primera página           
            await loadMobileData(); // Recarga datos móviles si es necesario  
          }
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: "error",
            title: "Error al eliminar usuario",
            message: "Ocurrió un error al intentar eliminar el usuario.",
            onConfirm: () =>
              setConfirmModal({ ...confirmModal, isOpen: false }),
          });
        }
      },
    });
  };

  // MOBILE VERSION (cards con paginación al backend)
  const [mobileUsers, setMobileUsers] = useState([]);
  const [mobilePage, setMobilePage] = useState(0);
  const [mobileTotal, setMobileTotal] = useState(0);

  const loadMobileData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const url = `${process.env.REACT_APP_API_URL}/users/get-all`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: mobilePage, size: pageSize },
      });

      setMobileUsers(response.data.data);
      setMobileTotal(response.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [mobilePage, pageSize]);

  useEffect(() => {
    if (isMobile) loadMobileData();
  }, [isMobile, loadMobileData]);

  const totalMobilePages = Math.ceil(mobileTotal / pageSize);

  // RENDER
  return (
    <>
      <TitleCard title="Usuarios" />

            {/**Boton crear */}
            <div className="flex justify-end my-1">
                <ActionButton
                    onClick={handleCreateUser}
                />
            </div>

      {/* DESKTOP: AG GRID */}
      {!isMobile && (
        <div
          className="relative ag-theme-material my-4"
          style={{ height: 530 }}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
              <Loader message="Cargando usuarios..." />
            </div>
          )}

          <AgGridReact
            rowModelType="infinite"
            pagination={true}
            paginationPageSize={pageSize}
            paginationPageSizeSelector={[10, 25, 50]}
            cacheBlockSize={pageSize}
            maxBlocksInCache={5}
            components={components}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            onPaginationChanged={onPaginationChanged}
          />
        </div>
      )}

      <MobileCardList
        isMobile={isMobile}
        loading={loading}
        loaderText="Cargando usuarios..."
        data={mobileUsers}
        mobilePage={mobilePage}
        setMobilePage={setMobilePage}
        totalPages={totalMobilePages}
        totalItems={mobileTotal}
        pageSize={pageSize}
        renderItem={(user) => (
          <div className="flex">
            <div className="flex-1 grid gap-4 grid-cols-1 [@media(min-width:430px)]:grid-cols-2">
              <h2 className="text-lg font-semibold col-span-full">
                {user.name}
              </h2>

              <p className="text-sm text-gray-600">{user.email}</p>
              <p className="text-sm text-gray-600">Usuario: {user.username}</p>
              <p className="text-sm text-gray-600">Edad: {user.age}</p>
            </div>

            <div className="flex flex-col justify-center items-end space-y-3">
              <button onClick={() => handleEditUser(user)}>
                <SquarePen size={18} color="#16A34A" strokeWidth={1.2} />
              </button>

              <button
                onClick={() => handleAssignRoles(user)}
                title="Asignar roles"
                aria-label="Asignar roles"
              >
                <ShieldCheck size={18} color="#2563EB" strokeWidth={1.2} />
              </button>

              <button
                onClick={() => handleAssignPermissions(user)}
                title="Asignar permisos"
                aria-label="Asignar permisos"
              >
                <KeyRound size={18} color="#2563EB" strokeWidth={1.2} />
              </button>

              <button onClick={() => handleDeleteUser({ data: user })}>
                <Trash2 size={18} color="#B91C1C" strokeWidth={1.2} />
              </button>
            </div>
          </div>
        )}
      />
      {/* MODAL EDITAR */}
      <ReusableModal
        isOpen={showEditModal}
        title="Editar Usuario"
        onClose={() => setShowEditModal(false)}
      >
        <UserForm
          initialData={selectedUser}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedUser(null);

            setConfirmModal({
              isOpen: true,
              type: "success",
              title: "Usuario actualizado",
              message: "Los datos del usuario se guardaron correctamente.",
              onConfirm: () =>
                setConfirmModal({ ...confirmModal, isOpen: false }),
            });

            if (gridApiRef.current?.purgeInfiniteCache) {
              setLoading(true);
              gridApiRef.current.purgeInfiniteCache();
            }
            // MOBILE
            if (isMobile) {
              loadMobileData();
            }
          }}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
        />
      </ReusableModal>

      {/* MODAL CONFIRMAR */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </>
  );
};

export default UsersList;
